import React, { useEffect, useState, useRef } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { Zap } from 'lucide-react';
import { toast } from 'sonner';
import { makeRESTApiRequest } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import CountyMunicipalityDatalistFields from '../CountyMunicipality/CountyMunicipalityDatalistFields';

const FLORIDA_STATE = 'Florida';

const emptyForm = () => ({
  municipality: '',
  county: '',
  standardMinDays: '',
  standardMaxDays: '',
  rush: false,
  rushMinDays: '',
  rushMaxDays: '',
  notes: ''
});

const rowToForm = row => ({
  municipality: row.municipality ?? '',
  county: row.county ?? '',
  standardMinDays: row.standardMinDays ?? '',
  standardMaxDays: row.standardMaxDays ?? '',
  rush: Boolean(row.rush),
  rushMinDays: row.rushMinDays != null ? row.rushMinDays : '',
  rushMaxDays: row.rushMaxDays != null ? row.rushMaxDays : '',
  notes: row.notes ?? ''
});

const buildPayload = (form, location) => {
  const payload = {
    municipality: location.municipality.trim(),
    county: location.county.trim(),
    state: FLORIDA_STATE,
    standardMinDays: Number(form.standardMinDays),
    standardMaxDays: Number(form.standardMaxDays),
    rush: form.rush,
    notes: form.notes?.trim() ? form.notes.trim() : undefined,
    active: true
  };

  if (form.rush) {
    payload.rushMinDays = Number(form.rushMinDays);
    payload.rushMaxDays = Number(form.rushMaxDays);
  } else {
    payload.rushMinDays = null;
    payload.rushMaxDays = null;
  }

  return payload;
};

const TurnaroundTimeFormModal = ({ show, mode, initialRecord, onHide, onSaved }) => {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const locationRef = useRef({ municipality: '', county: '' });

  const title = mode === 'add' ? 'Add Turnaround Time' : 'Edit Turnaround Time';
  const submitLabel = mode === 'add' ? 'Add' : 'Update';

  useEffect(() => {
    if (!show) return;
    setErrors({});
    if (mode === 'edit' && initialRecord) {
      setForm(rowToForm(initialRecord));
    } else {
      const formValues = emptyForm();
      formValues.county = initialRecord.county;
      setForm(formValues);
    }
  }, [show, mode, initialRecord]);

  useEffect(() => {
    if (!show) return;
    locationRef.current = {
      municipality: form.municipality?.trim() ?? '',
      county: form.county?.trim() ?? ''
    };
  }, [show, form.municipality, form.county]);

  const update = (key, value) => {
    setForm(prev => {
      const next = { ...prev, [key]: value };
      if (key === 'rush' && !value) {
        next.rushMinDays = '';
        next.rushMaxDays = '';
      }
      return next;
    });
    if (errors[key]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const validate = () => {
    const next = {};
    const loc = locationRef.current;
    if (!loc.municipality?.trim()) next.municipality = 'Required';
    if (!loc.county?.trim()) next.county = 'Required';

    const smin = Number(form.standardMinDays);
    const smax = Number(form.standardMaxDays);
    if (form.standardMinDays === '' || Number.isNaN(smin) || smin < 1) {
      next.standardMinDays = 'Enter a valid number (min 1)';
    }
    if (form.standardMaxDays === '' || Number.isNaN(smax) || smax < 1) {
      next.standardMaxDays = 'Enter a valid number (min 1)';
    }
    if (!next.standardMinDays && !next.standardMaxDays && !Number.isNaN(smin) && !Number.isNaN(smax) && smin > smax) {
      next.standardMaxDays = 'Must be greater than or equal to min';
    }

    if (form.rush) {
      const rmin = Number(form.rushMinDays);
      const rmax = Number(form.rushMaxDays);
      if (form.rushMinDays === '' || Number.isNaN(rmin) || rmin < 1) {
        next.rushMinDays = 'Required when rush is enabled';
      }
      if (form.rushMaxDays === '' || Number.isNaN(rmax) || rmax < 1) {
        next.rushMaxDays = 'Required when rush is enabled';
      }
      if (!next.rushMinDays && !next.rushMaxDays && !Number.isNaN(rmin) && !Number.isNaN(rmax) && rmin > rmax) {
        next.rushMaxDays = 'Must be greater than or equal to min';
      }
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    const payload = buildPayload(form, locationRef.current);

    const { response, error } =
      mode === 'add'
        ? await makeRESTApiRequest({
            endpoint: ENDPOINTS.CREATE_TURNAROUND_TIMES,
            method: 'POST',
            requestBody: payload
          })
        : await makeRESTApiRequest({
            endpoint: ENDPOINTS.TURNAROUND_TIMES_WITH_ID(initialRecord._id),
            method: 'PUT',
            requestBody: payload
          });

    setSubmitting(false);

    if (error) {
      toast.error(error);
      return;
    }

    toast.success(mode === 'add' ? 'Turnaround time added' : 'Turnaround time updated');
    onSaved?.(response);
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton className="border-bottom bg-white">
          <Modal.Title as="h5" className="mb-0 font-weight-bold text-dark">
            {title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-white">
          <div className="mb-3">
            <CountyMunicipalityDatalistFields
              key={initialRecord?._id ? String(initialRecord._id) : 'turnaround-new'}
              variant="countyAndMunicipality"
              layout="row"
              columnOrder="cityFirst"
              creatable
              cityRequired
              countyRequired
              initialCity={form.municipality}
              initialCounty={form.county}
              disabled={submitting}
              cityFieldLabel="Municipality"
              countyFieldLabel="County"
              inputClassName="county-input-custom form-control form-control-lg"
              onFormChange={({ county, city }) => {
                locationRef.current = {
                  municipality: (city ?? '').trim(),
                  county: (county ?? '').trim()
                };
              }}
            />
            {(errors.municipality || errors.county) && (
              <div className="text-danger small mt-2">{errors.municipality || errors.county}</div>
            )}
          </div>

          <Row>
            <Col md={6}>
              <Form.Group className="midFont mb-3">
                <Form.Label className="font-weight-medium text-dark">
                  Standard Min Days <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  size="lg"
                  value={form.standardMinDays}
                  onChange={e => update('standardMinDays', e.target.value)}
                  isInvalid={!!errors.standardMinDays}
                  disabled={submitting}
                />
                <Form.Control.Feedback type="invalid">{errors.standardMinDays}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="midFont mb-3">
                <Form.Label className="font-weight-medium text-dark">
                  Standard Max Days <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  size="lg"
                  value={form.standardMaxDays}
                  onChange={e => update('standardMaxDays', e.target.value)}
                  isInvalid={!!errors.standardMaxDays}
                  disabled={submitting}
                />
                <Form.Control.Feedback type="invalid">{errors.standardMaxDays}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="midFont mb-3">
            <Form.Check
              type="checkbox"
              id="turnaround-rush"
              checked={form.rush}
              onChange={e => update('rush', e.target.checked)}
              disabled={submitting}
              label={
                <span className="d-inline-flex align-items-center text-dark">
                  <Zap size={16} className="mr-2 text-warning flex-shrink-0" aria-hidden />
                  Rush service available
                </span>
              }
            />
          </Form.Group>

          {form.rush && (
            <Row>
              <Col md={6}>
                <Form.Group className="midFont mb-3">
                  <Form.Label className="font-weight-medium text-dark">
                    Rush Min Days <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    size="lg"
                    value={form.rushMinDays}
                    onChange={e => update('rushMinDays', e.target.value)}
                    isInvalid={!!errors.rushMinDays}
                    disabled={submitting}
                  />
                  <Form.Control.Feedback type="invalid">{errors.rushMinDays}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="midFont mb-3">
                  <Form.Label className="font-weight-medium text-dark">
                    Rush Max Days <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    size="lg"
                    value={form.rushMaxDays}
                    onChange={e => update('rushMaxDays', e.target.value)}
                    isInvalid={!!errors.rushMaxDays}
                    disabled={submitting}
                  />
                  <Form.Control.Feedback type="invalid">{errors.rushMaxDays}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
          )}

          <Form.Group className="midFont mb-3">
            <Form.Label className="font-weight-medium text-dark">Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              size="lg"
              value={form.notes}
              onChange={e => update('notes', e.target.value)}
              placeholder="Optional notes"
              disabled={submitting}
              style={{ resize: 'vertical' }}
            />
          </Form.Group>

        </Modal.Body>
        <Modal.Footer className="bg-light border-top">
          <Button variant="outline-secondary" size="md" type="button" onClick={onHide} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="success" size="md" type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : submitLabel}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default TurnaroundTimeFormModal;
